import React, { useEffect, useMemo, useRef, useState } from 'react'
import { RefreshCw } from 'lucide-react'
import { Link } from 'react-router-dom'
import { api } from '../lib/api'
import InlineError from '../ui/InlineError'
import Loading from '../ui/Loading'
import { formatDateTime } from '../lib/date'
import MenuGuideButton from '../components/MenuGuideButton'

const TABS = [
  { key: 'change', label: '변경이력' },
  { key: 'event', label: '이벤트이력' },
  { key: 'alimtalk', label: '알림톡발송' },
  { key: 'fax', label: '팩스발송' },
  { key: 'consent', label: '동의이력' },
  { key: 'bulkSms', label: '단체문자 발송' },
  { key: 'bulkKakao', label: '카카오톡 단체발송' },
  { key: 'email', label: '이메일발송' },
]

const AUDIT_FETCH_LIMIT = 1000

const NOTICE_CHANNEL_FILTER = {
  alimtalk: ['ALIMTALK', 'KAKAO', 'KAKAO_SMS'],
  bulkSms: ['SMS'],
  bulkKakao: ['KAKAO_BULK', 'KAKAO', 'KAKAO_SMS', 'ALIMTALK'],
  email: ['EMAIL', 'MAIL'],
}
const NOTICE_LOG_TABS = new Set(Object.keys(NOTICE_CHANNEL_FILTER))

const EVENT_ACTION_KEYWORDS = ['LOGIN', 'VIEW', 'READ', 'ACCESS', 'EXPORT', 'DOWNLOAD']
const EVENT_ACTION_CODES = new Set([
  'CUSTOMER_MGMT_LIST',
  'CUSTOMER_MGMT_DETAIL',
])

/** 이벤트이력 탭에서 숨길 감사 로그 (노이즈·민감도 높은 고빈도 이벤트) */
function isEventTabHiddenAction(row) {
  const action = String(row?.action || '').toUpperCase()
  if (action === 'REFRESH_TOKEN_SUCCESS') return true
  if (action === 'LOGIN_SUCCESS' || action === 'LOGIN_FAILURE') return true
  return false
}

/**
 * 이벤트이력 탭 표시 여부: 조회/접근류 + 인증 세션 관련(단, 숨김 액션 제외).
 */
function shouldShowInEventTab(row) {
  if (isEventTabHiddenAction(row)) return false
  if (isEventAction(row?.action)) return true
  if (isAuthSessionNoise(row)) return true
  return false
}

/** 세션 유지용 API 등 — 도메인 데이터 변경과 무관한 감사 로그 (변경이력 탭에서 제외) */
function isAuthSessionNoise(row) {
  const action = String(row?.action || '').toUpperCase()
  if (action.startsWith('REFRESH_TOKEN_')) return true
  const res = String(row?.resource || '').toLowerCase().replace(/\\/g, '/')
  if (res.includes('/auth/refresh')) return true
  return false
}

/** 감사 로그 API의 metaJson (파일 업로드 맥락 등) */
function parseAuditMeta(row) {
  try {
    const raw = String(row?.metaJson ?? '').trim()
    if (!raw || !raw.startsWith('{')) return {}
    const obj = JSON.parse(raw)
    return obj && typeof obj === 'object' && !Array.isArray(obj) ? obj : {}
  } catch {
    return {}
  }
}

function auditRowSourceChannel(row) {
  const top = row?.sourceChannel
  if (top != null && String(top).trim() !== '') return top
  const ch = parseAuditMeta(row)?.sourceChannel
  return ch
}

function formatAuditBytes(n) {
  const x = Number(n)
  if (!Number.isFinite(x) || x < 0) return n != null && n !== '' ? String(n) : ''
  if (x < 1024) return `${Math.round(x)} B`
  if (x < 1024 * 1024) return `${(x / 1024).toFixed(1)} KB`
  return `${(x / (1024 * 1024)).toFixed(1)} MB`
}

const ACTION_LABELS = {
  LOGIN_SUCCESS: '로그인 성공',
  LOGIN_FAILURE: '로그인 실패',
  LOGIN_LOCKED: '로그인 잠금',
  LOGIN_BLOCKED_STATUS: '로그인 차단',
  REFRESH_TOKEN_SUCCESS: '액세스 토큰 갱신(성공)',
  REFRESH_TOKEN_FAILURE: '액세스 토큰 갱신(실패)',
  REFRESH_TOKEN_BLOCKED_STATUS: '액세스 토큰 갱신(차단)',
  USER_CREATE: '사용자 생성',
  USER_UPDATE: '사용자 정보 변경',
  USER_ACTIVATE: '사용자 활성화',
  USER_DEACTIVATE: '사용자 비활성화',
  USER_PASSWORD_RESET: '비밀번호 초기화',
  ADD_GA_BRANCH_MAPPING: '원수사 담당자↔GA 매핑 추가',
  REMOVE_GA_BRANCH_MAPPING: '원수사 담당자↔GA 매핑 제거',
  REQUEST_CREATED: '의뢰 생성',
  REQUEST_ASSIGNED: '의뢰 배정',
  REQUEST_REASSIGN: '의뢰 재배정',
  REQUEST_COMPLETE: '의뢰 타겟 완료',
  REQUEST_COMPLETED: '의뢰 완료',
  REQUEST_CANCEL: '의뢰 취소',
  REQUEST_REREQUEST: '의뢰 재요청',
  REQUEST_APPROVE_COMPLETION: '의뢰 완료 승인',
  REQUEST_TARGET_START_PROGRESS: '타겟 진행 시작',
  REQUEST_TARGET_QUESTION: '문의 등록',
  REQUEST_TARGET_QUESTION_RESOLVED: '문의 해결',
  REQUEST_TARGET_CANCEL: '타겟 취소',
  NOTICE_APPROVE: '공지 승인',
  NOTICE_SEND: '공지 발송',
  HEALTH_REPORT_CREATE: '진료분석 리포트 생성',
  HEALTH_REPORT_VIEW: '진료분석 리포트 조회',
  HEALTH_REPORT_REANALYZE: '진료분석 재분석',
  HEALTH_REPORT_EXPORT: '진료분석 결과 다운로드',
  FILE_UPLOAD: '파일 업로드',
  FILE_UPLOAD_ATTEMPT: '파일 업로드 시도',
  FILE_UPLOAD_FAILED: '파일 업로드 실패',
  FILE_DELETE: '파일 삭제',
  FILE_DELETE_ATTEMPT: '파일 삭제 시도',
  FILE_DELETE_FAILED: '파일 삭제 실패',
  FILE_FORCE_DELETE: '파일 강제 삭제(운영자)',
  FILE_FORCE_DELETE_ATTEMPT: '파일 강제 삭제 시도',
  FILE_FORCE_DELETE_FAILED: '파일 강제 삭제 실패',
  PASSWORD_RESET_REQUEST: '비밀번호 재설정 메일 발송',
  PASSWORD_RESET_REQUEST_MISMATCH: '비밀번호 재설정 요청(정보 불일치)',
  PASSWORD_RESET_SUCCESS: '비밀번호 재설정 완료',
  USER_PASSWORD_CHANGE: '비밀번호 변경',
  USER_CREATE_GA_PROXY: 'GA 대리 사용자 생성',
  USER_IMPORT_UPSERT_CREATE: '사용자 일괄 등록(신규)',
  USER_IMPORT_UPSERT_UPDATE: '사용자 일괄 등록(수정)',
  USER_WITHDRAWAL_REQUEST: '탈퇴 요청',
  USER_WITHDRAWAL: '탈퇴 처리',
  SET_GA_BRANCH_MAPPING: '원수사 담당자↔GA 매핑 설정',
  REQUEST_REASSIGN_REQUESTED: '의뢰 재배정 요청',
  REQUEST_COMMENT_BROADCAST: '의뢰 코멘트 전파',
  REQUEST_COMPLETED: '의뢰 완료',
  HEALTH_REPORT_PDF_EXPORT: '진료분석 PDF 다운로드',
  HEALTH_REPORT_RAW_EXPORT: '진료분석 원본 다운로드',
  HEALTH_REPORT_SUMMARY_EXPORT: '진료분석 요약 다운로드',
  HEALTH_COUNSELING_GENERATE: '진료분석 상담안 생성',
  HEALTH_COUNSELING_DELETE: '진료분석 상담안 삭제',
  HEALTH_SCOPE_ACCESS_DENIED: '진료분석 접근 거부',
  DIAGNOSIS_SIMULATION_CREATED: '진단 시뮬레이션 생성',
  DIAGNOSIS_SIMULATION_GENERATE: '진단 시뮬레이션 생성',
  CUSTOMER_MGMT_LIST: '고객관리 · 목록 조회',
  CUSTOMER_MGMT_DETAIL: '고객관리 · 상세 조회',
  CUSTOMER_MGMT_CREATE: '고객관리 · 고객 생성',
  CUSTOMER_MGMT_PROFILE: '고객관리 · 프로필 수정',
  CUSTOMER_MGMT_MEMO: '고객관리 · 메모',
  CUSTOMER_MGMT_ANNIVERSARY: '고객관리 · 기념일',
  CUSTOMER_MGMT_ANNIVERSARY_DELETE: '고객관리 · 기념일 삭제',
  CUSTOMER_MGMT_FAMILY: '고객관리 · 가족 정보',
  CUSTOMER_MGMT_FAMILY_DELETE: '고객관리 · 가족 정보 삭제',
  CUSTOMER_MGMT_BUSINESS_CARD: '고객관리 · 명함',
  CUSTOMER_MGMT_BUSINESS_CARD_DELETE: '고객관리 · 명함 삭제',
  CUSTOMER_MGMT_MERGE: '고객관리 · 고객 병합',
  CUSTOMER_AI_MESSAGE_DRAFT: '고객관리 · AI 메시지 초안',
  CUSTOMER_CONTACT_MESSAGE_DRAFT: '고객관리 · 연락 메시지 초안',
  CUSTOMER_EASY_EXPLANATION: '고객관리 · 쉬운 설명',
  CUSTOMER_COMPLIANCE_RISK_CHECK: '고객관리 · 준법·리스크 확인',
  CUSTOMER_COVERAGE_TERMS_LINK: '고객관리 · 약관 연결',
  CUSTOMER_COVERAGE_TERMS_UNLINK: '고객관리 · 약관 연결 해제',
  CUSTOMER_COVERAGE_EXCEL_IMPORT: '고객관리 · 보장 엑셀 가져오기',
  CUSTOMER_COVERAGE_ANALYZE: '고객관리 · 보장 분석',
  CUSTOMER_COVERAGE_SAVE: '고객관리 · 보장 저장',
  CUSTOMER_COVERAGE_UPDATE: '고객관리 · 보장 수정',
  CUSTOMER_COVERAGE_DELETE: '고객관리 · 보장 삭제',
  CUSTOMER_COVERAGE_RESET: '고객관리 · 보장 초기화',
  CUSTOMER_COVERAGE_CANDIDATE_LINK: '고객관리 · 계약 후보 연결',
  CUSTOMER_COVERAGE_CANDIDATE_IGNORE: '고객관리 · 계약 후보 제외',
  CUSTOMER_INTEGRATED_REPORT_GENERATE: '고객관리 · 통합 리포트 생성',
  CUSTOMER_MATURITY_EXCEL_IMPORT: '고객관리 · 만기 엑셀 가져오기',
  CLAIM_INTAKE_LINK: '보험금청구 · 접수 링크',
  CLAIM_INTAKE_LINK_REQUEST: '보험금청구 · 접수 링크 요청',
  CLAIM_INTAKE_EXPIRE_NOW: '보험금청구 · 접수 만료',
  CLAIM_INTAKE_EXTEND_EXPIRY: '보험금청구 · 접수 기한 연장',
  CLAIM_INTAKE_FINALIZE_SUCCESS: '보험금청구 · 접수 완료',
  CLAIM_INTAKE_FINALIZE_FAILED: '보험금청구 · 접수 실패',
  RECOVERY_ID: '계정 복구 요청',
  변경이력: '변경이력',
}

const STATUS_LABELS = {
  SENT: '발송완료',
  FAILED: '실패',
  PENDING: '대기',
  SUBMITTED: '접수',
  DELIVERED: '전달완료',
  CANCELLED: '취소',
  QUEUED: '대기열',
  SKIPPED: '건너뜀',
}

/** notification_logs.channel (발송 경로) */
const NOTIFICATION_CHANNEL_LABELS = {
  KAKAO: '카카오 알림톡',
  KAKAO_SMS: '카카오 알림톡 후 SMS',
  ALIMTALK: '알림톡',
  SMS: 'SMS',
  NONE: '미시도/없음',
  KAKAO_BULK: '카카오 단체발송',
  EMAIL: '이메일',
  MAIL: '이메일',
  FCM: '앱 푸시',
  FAX: '팩스',
  POPBILL: '팝빌 팩스',
}

const SOURCE_CHANNEL_LABELS = {
  PC: 'PC',
  MOBILE_WEB: '모바일 웹',
  APP: '앱',
  WINDOW: 'Windows',
  SYSTEM: '시스템',
  UNKNOWN: '미분류',
}

const CONSENT_CHANNEL_LABELS = {
  WEB: '웹',
  CUSTOMER_WEB: '고객 웹',
  PC: 'PC',
  APP: '앱',
  IOS_APP: 'iOS 앱',
  ANDROID_APP: 'Android 앱',
  MOBILE_WEB: '모바일 웹',
  WINDOW: 'Windows',
}

const CONSENT_DOC_LABELS = {
  TERMS: '이용약관',
  PRIVACY: '개인정보 처리방침',
  MARKETING: '마케팅·광고 수신 동의',
  REFUND_POLICY: '환불·취소 정책',
  HEALTH_INQUIRY_PRIVACY: '조회 개인정보 동의',
  HEALTH_INQUIRY_SENSITIVE: '민감정보·고유식별정보 동의',
  HEALTH_INQUIRY_CREDIT: '보험·의료비 개인(신용)정보 동의',
  HEALTH_INQUIRY_AGENT_CONFIRM: '고객 동의 확인',
  CLAIM_PRIVACY: '청구 개인정보 수집·이용',
  CLAIM_SENSITIVE_UNIQUE_ID: '청구 민감정보·고유식별정보',
  CLAIM_THIRD_PARTY_PROVISION: '청구 제3자 제공',
  privacy: '조회 개인정보 동의',
  sensitive: '민감정보·고유식별정보',
  creditInfo: '보험·의료비 개인(신용)정보',
  customerAuth: '고객 동의 확인',
}

const CONSENT_SERVICE_LABELS = {
  HIDDEN_INSURANCE: '숨은보험금찾기',
  HEALTH_ANALYSIS: '진료정보분석',
  claim: '보험금청구',
}

const UPLOAD_SOURCE_LABELS = {
  PUBLIC_LANDING: '카테고리 랜딩 · 템플릿/페이지 이미지',
  PUBLIC_PROFILE: '모바일명함 · 프로필 이미지',
  PUBLIC_CARD_ORG_LOGO: '모바일명함 · 조직 로고',
  BANNER: '운영 배너 이미지',
  EXTERNAL_PROGRAM: '외부 프로그램 배포',
  REQUEST_ATTACHMENT: '설계 의뢰 첨부',
  GENERIC: '일반 파일 업로드',
  UNKNOWN: '파일 (출처 미상)',
}

const CUSTOMER_MGMT_RESOURCE_SEGMENTS = {
  list: '목록 조회',
  create: '고객 생성',
  merge: '고객 병합',
  detail: '상세 조회',
  profile: '프로필 수정',
  memo: '메모',
  anniversary: '기념일',
  'anniversary-delete': '기념일 삭제',
  family: '가족 정보',
  'family-delete': '가족 정보 삭제',
  'ai-message': 'AI 메시지 초안',
  'contact-message': '연락 메시지 초안',
  'easy-explanation': '쉬운 설명',
  'compliance-risk': '준법·리스크 확인',
  'business-card': '명함',
  'business-card-delete': '명함 삭제',
  'coverage-analysis': '보장 분석',
  'coverage-contracts': '보장·계약',
  'coverage-contract-candidates': '계약 후보',
  'integrated-report': '통합 리포트',
  'diagnosis-simulation': '진단 시뮬레이션',
  'maturity-policies': '만기 정책',
}

const SOCIAL_PROVIDER_LABELS = {
  KAKAO: '카카오',
  NAVER: '네이버',
  GOOGLE: 'Google',
  APPLE: 'Apple',
}

function labelConsentChannel(raw) {
  const code = String(raw || '').trim().toUpperCase()
  if (!code) return '-'
  return CONSENT_CHANNEL_LABELS[code] || SOURCE_CHANNEL_LABELS[code] || raw
}

function labelConsentDoc(raw) {
  const code = String(raw || '').trim()
  if (!code) return '동의항목'
  return CONSENT_DOC_LABELS[code] || CONSENT_DOC_LABELS[code.toUpperCase()] || code
}

function formatConsentDocSummary(raw) {
  const text = String(raw || '').trim()
  if (!text) return '-'
  if (text.includes('|')) {
    return text.split('|').map((part) => {
      const idx = part.indexOf(':')
      if (idx <= 0) return part
      const doc = part.slice(0, idx)
      const ver = part.slice(idx + 1)
      return `${labelConsentDoc(doc)} (${ver})`
    }).join(' · ')
  }
  const idx = text.indexOf(':')
  if (idx > 0) {
    const doc = text.slice(0, idx)
    const ver = text.slice(idx + 1)
    if (/^[A-Z0-9_]+$/.test(doc)) return `${labelConsentDoc(doc)} (${ver})`
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return `동의 묶음 ${text}`
  return text
}

function formatConsentTargetId(raw) {
  const text = String(raw || '').trim()
  if (!text || text === '-') return '-'
  const idx = text.indexOf(':')
  if (idx <= 0) return text
  const kind = text.slice(0, idx)
  const id = text.slice(idx + 1)
  const kindLabel = CONSENT_SERVICE_LABELS[kind] || CONSENT_SERVICE_LABELS[kind.toLowerCase()] || kind
  if (/^\d+$/.test(id)) return `${kindLabel} #${id}`
  return `${kindLabel} · ${id}`
}

function labelUploadSource(raw) {
  const code = String(raw || '').trim().toUpperCase()
  if (!code) return '-'
  return UPLOAD_SOURCE_LABELS[code] || `파일 (${code})`
}

function formatAuditResource(raw) {
  const s = String(raw || '').trim()
  if (!s) return '-'

  const cm = s.match(/^customer-management:([^:]+)(?::(.*))?$/)
  if (cm) {
    const segKey = cm[1]
    const seg = CUSTOMER_MGMT_RESOURCE_SEGMENTS[segKey] || segKey
    const rest = cm[2]
    let base = `고객관리 · ${seg}`
    if (!rest) return base
    const parts = rest.split(':')
    if (segKey === 'coverage-contracts') {
      if (parts[1] === 'excel') return `${base} · 엑셀 가져오기 (고객 #${parts[0]})`
      if (parts[1] === 'reset') return `${base} · 초기화 (고객 #${parts[0]})`
      if (parts[2] === 'terms-link') {
        return `${base} · 약관 연결 (고객 #${parts[0]}, 계약 #${parts[1]})`
      }
      if (parts.length >= 2 && /^\d+$/.test(parts[0]) && /^\d+$/.test(parts[1])) {
        return `${base} · 계약 #${parts[1]} (고객 #${parts[0]})`
      }
    }
    if (segKey === 'coverage-contract-candidates' && parts.length >= 3) {
      const action = parts[2] === 'link' ? '연결' : parts[2] === 'ignore' ? '제외' : parts[2]
      return `고객관리 · 계약 후보 ${action} (고객 #${parts[0]}, 후보 #${parts[1]})`
    }
    if (/^\d+$/.test(parts[0])) return `${base} (#${parts[0]})`
    return `${base} · ${parts.join(' · ')}`
  }

  const usersMatch = s.match(/^users:(\d+)/)
  if (usersMatch) return `사용자 #${usersMatch[1]}`

  const simplePrefixes = [
    ['request:', '설계 의뢰'],
    ['target:', '의뢰 타겟'],
    ['healthReport:', '진료분석 리포트'],
    ['user:', '사용자'],
    ['notice:', '공지'],
    ['session:', '청구 접수 세션'],
    ['org:', '조직'],
    ['me:', '본인 계정'],
    ['file:', '파일'],
    ['files/', '파일 '],
  ]
  for (const [prefix, label] of simplePrefixes) {
    if (s.startsWith(prefix)) {
      const id = s.slice(prefix.length).split(':')[0]
      return id ? `${label} #${id}` : label
    }
  }

  if (s === 'auth:password-reset') return '비밀번호 재설정'
  if (s === 'auth:recovery-id') return '계정 복구'
  if (s.includes('/auth/refresh')) return '세션 토큰 갱신'
  if (s.startsWith('/')) return s.replace(/^\/v1\//, '').replace(/\//g, ' · ')
  return s
}

function labelForActionCode(code) {
  if (!code) return '-'
  if (ACTION_LABELS[code]) return ACTION_LABELS[code]
  const upper = String(code).toUpperCase()
  if (upper.startsWith('SOCIAL_LOGIN_SUCCESS_')) {
    const provider = upper.slice('SOCIAL_LOGIN_SUCCESS_'.length)
    return `소셜 로그인 성공 (${SOCIAL_PROVIDER_LABELS[provider] || provider})`
  }
  if (upper.startsWith('SOCIAL_LOGIN_')) {
    const tail = upper.slice('SOCIAL_LOGIN_'.length)
    const tailMap = {
      FAILED: '소셜 로그인 실패',
      PENDING: '소셜 로그인 대기',
      UNLINKED: '소셜 계정 미연동',
      BLOCKED_STATUS: '소셜 로그인 차단',
    }
    if (tailMap[tail]) return tailMap[tail]
  }
  if (upper.includes('LOGIN')) return '로그인'
  if (upper.includes('VIEW') || upper.includes('READ')) return '조회'
  if (upper.includes('ACCESS')) return '접근'
  if (upper.includes('EXPORT') || upper.includes('DOWNLOAD')) return '내보내기'
  if (upper.includes('CREATE')) return '생성'
  if (upper.includes('UPDATE')) return '수정'
  if (upper.includes('DELETE') || upper.includes('REMOVE')) return '삭제'
  if (upper.includes('APPROVE')) return '승인'
  if (upper.includes('ASSIGN')) return '배정'
  if (upper.includes('CANCEL')) return '취소'
  if (upper.includes('SEND')) return '발송'
  if (upper.startsWith('FILE_')) {
    if (upper.includes('FORCE')) return '파일(운영자 강제삭제)'
    if (upper.includes('UPLOAD')) return upper.includes('ATTEMPT') ? '파일 업로드 시도' : upper.includes('FAILED') ? '파일 업로드 실패' : '파일 업로드'
    if (upper.includes('DELETE')) return upper.includes('ATTEMPT') ? '파일 삭제 시도' : upper.includes('FAILED') ? '파일 삭제 실패' : '파일 삭제'
  }
  return '기타'
}

/** 알림톡 템플릿 코드 → 업무 메뉴 성격 (표시용) */
const ALIMTALK_MENU_BY_TEMPLATE = {
  REQUEST_ASSIGNED: '설계 의뢰 · 배정 알림',
  NOTICE_APPROVED: '공지 · 승인/발송',
  NOTICE_SEND: '공지 · 발송',
  DAILY_WORK_BRIEFING_V2: '오늘의 업무 브리핑',
  AFTER_CONTRACT_CERTIFICATE: '보험금 청구 · 증권/서류 요청',
  CLAIM_INTAKE_LINK: '보험금 청구 · 접수 링크',
  CLAIM_INTAKE_SUBMITTED: '보험금 청구 · 접수 완료',
  CLAIM_SIGNATURE_REQUEST: '보험금 청구 · 전자서명 요청',
  CLAIM_COMPLETED_CUSTOMER: '보험금 청구 · 완료 안내',
  CLAIM_COMPLETED_PLANNER: '보험금 청구 · 완료 알림',
  CLAIM_FAX_SUBMITTED: '보험금 청구 · 팩스 접수',
  CLAIM_FAX_RESULT: '보험금 청구 · 팩스 결과',
  HEALTH_AUTH_REQUEST: '진료정보분석 · 고객 인증',
  OPERATOR_SECURITY_ALERT: '운영 보안 알림',
}

function alimtalkMenuLabel(templateCode) {
  const c = String(templateCode || '').trim().toUpperCase()
  if (!c) return '-'
  return ALIMTALK_MENU_BY_TEMPLATE[c] || `기타 알림 (${templateCode})`
}

/**
 * SOLAPI 등 게이트웨이 toString 장문을 운영 화면용으로 압축.
 * 실제 알림톡 변수 치환 문구와는 별개(그건 대개 DB에 안 남음).
 */
/** API `recipients` 배열 정규화 (구형 응답 호환) */
function normalizeNoticeRecipients(row) {
  if (Array.isArray(row?.recipients) && row.recipients.length > 0) return row.recipients
  return []
}

function formatRecipientOneLine(r) {
  if (!r || typeof r !== 'object') return '-'
  const kind = String(r.orgTypeLabel || r.orgType || '').trim()
  const org = String(r.orgName || '').trim()
  const name = String(r.name || '').trim()
  const phone = [r.phoneTail, r.phoneMask].map(x => String(x || '').trim()).filter(Boolean).join(' · ')
  const bits = [kind, org, name].filter(Boolean)
  if (bits.length) return `${bits.join(' · ')}${phone ? ` (${phone})` : ''}`
  if (phone) return phone
  if (r.userId != null && String(r.userId).trim() !== '') return `사용자 ID ${r.userId}`
  return '-'
}

function summarizeIntegrationResponse(raw) {
  if (raw == null) return ''
  const s = String(raw).trim()
  if (!s) return ''
  const bulky = s.length > 120 && /MultipleDetailMessageSentResponse|GroupInfo\(|CountForCharge|GroupCount|failedMessageList=/.test(s)
  if (bulky) {
    const rs = s.match(/registeredSuccess=(\d+)/)
    const rf = s.match(/registeredFailed=(\d+)/)
    const parts = []
    if (rs) parts.push(`접수 성공 ${rs[1]}건`)
    if (rf && rf[1] !== '0') parts.push(`접수 실패 ${rf[1]}건`)
    if (parts.length) return parts.join(', ')
    return '외부 연동 응답 수신(원문 생략)'
  }
  if (s.length > 200) return `${s.slice(0, 200)}…`
  return s
}

function getLocalDateString(offsetDays = 0) {
  const d = new Date()
  d.setDate(d.getDate() + offsetDays)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function getDefaultRange() {
  return { from: getLocalDateString(-1), to: getLocalDateString(0) }
}

function toIsoOrEmpty(v, endOfDay = false) {
  if (!v) return ''
  try {
    const d = new Date(v)
    if (Number.isNaN(d.getTime())) return ''
    if (endOfDay) {
      d.setHours(23, 59, 59, 999)
    } else {
      d.setHours(0, 0, 0, 0)
    }
    return d.toISOString()
  } catch {
    return ''
  }
}

function containsBulkWord(item) {
  const source = `${item?.templateCode || ''} ${item?.title || ''} ${item?.messageText || ''} ${item?.rawMessage || ''}`.toLowerCase()
  return source.includes('bulk') || source.includes('group') || source.includes('단체')
}

function isEventAction(action) {
  const code = String(action || '').toUpperCase()
  if (!code) return false
  if (EVENT_ACTION_CODES.has(code)) return true
  return EVENT_ACTION_KEYWORDS.some(k => code.includes(k))
}

export default function OpsHistoryManagementPage() {
  const [tab, setTab] = useState('change')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [q, setQ] = useState('')
  const [status, setStatus] = useState('ALL')
  const [from, setFrom] = useState(getDefaultRange().from)
  const [to, setTo] = useState(getDefaultRange().to)
  const [page, setPage] = useState(0)
  const [size] = useState(20)
  const [result, setResult] = useState({ items: [], total: 0, actionCodes: [], templateCodes: [] })
  const [actionFilter, setActionFilter] = useState('')
  const [sourceChannel, setSourceChannel] = useState('ALL')
  const [requestIdFilter, setRequestIdFilter] = useState('')
  const [templateFilter, setTemplateFilter] = useState('ALL')
  const [detailRow, setDetailRow] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState(null)
  const [refreshKey, setRefreshKey] = useState(0)
  const detailRequestSeq = useRef(0)

  useEffect(() => {
    setPage(0)
    const range = getDefaultRange()
    setFrom(range.from)
    setTo(range.to)
  }, [tab])

  useEffect(() => {
    setActionFilter('')
    setSourceChannel('ALL')
    setRequestIdFilter('')
    setTemplateFilter('ALL')
    detailRequestSeq.current += 1
    setDetailRow(null)
    setDetailLoading(false)
    setDetailError(null)
  }, [tab])

  useEffect(() => {
    let ignore = false
    async function load() {
      setLoading(true)
      setError(null)
      try {
        let items = []
        let total = 0
        if (tab === 'change' || tab === 'event') {
          const params = new URLSearchParams()
          if (q) params.set('q', q)
          if (from) params.set('from', toIsoOrEmpty(from, false))
          if (to) params.set('to', toIsoOrEmpty(to, true))
          params.set('page', '0')
          params.set('size', String(AUDIT_FETCH_LIMIT))
          const res = await api.get(`/audit-logs?${params.toString()}`)
          const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
          const raw = Array.isArray(payload?.content) ? payload.content : []
          const baseFiltered = raw.filter((row) => {
            const noise = isAuthSessionNoise(row)
            if (tab === 'event') return shouldShowInEventTab(row)
            return !isEventAction(row?.action) && !noise
          })
          const actionCodes = Array.from(
            new Set(baseFiltered.map((row) => String(row?.action || '')).filter(Boolean))
          ).sort()
          const filtered = baseFiltered
            .filter((row) => !actionFilter || String(row?.action || '') === actionFilter)
            .filter((row) => sourceChannel === 'ALL' || normalizeSourceChannel(auditRowSourceChannel(row)) === sourceChannel)
          total = filtered.length
          const start = page * size
          items = filtered.slice(start, start + size)
          if (!ignore) setResult({ items, total, actionCodes })
          return
        } else if (tab === 'fax') {
          const params = { page, size }
          if (q) params.q = q
          if (status && status !== 'ALL') params.status = status
          if (from) params.from = toIsoOrEmpty(from, false)
          if (to) params.to = toIsoOrEmpty(to, true)
          const res = await api.get('/fax/transmissions', { params })
          const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
          const raw = Array.isArray(payload?.items) ? payload.items : []
          const serverTotal = Number(payload?.total)
          items = raw
          items = items.filter((row) => sourceChannel === 'ALL' || normalizeSourceChannel(row?.sourceChannel) === sourceChannel)
          total = sourceChannel !== 'ALL'
            ? items.length
            : (Number.isFinite(serverTotal) ? serverTotal : items.length)
        } else if (tab === 'consent') {
          const params = { page, size }
          if (q) params.q = q
          if (from) params.from = toIsoOrEmpty(from, false)
          if (to) params.to = toIsoOrEmpty(to, true)
          const res = await api.get('/operator/consent-audit-logs', { params })
          const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
          items = Array.isArray(payload?.items) ? payload.items : []
          total = Number(payload?.total) || items.length
        } else {
          const params = { page: page + 1, size }
          if (q) params.q = q
          if (from) params.from = toIsoOrEmpty(from, false)
          if (to) params.to = toIsoOrEmpty(to, true)
          const channels = NOTICE_CHANNEL_FILTER[tab] || []
          if (channels.length > 0) params.channel = channels.join(',')
          if (status && status !== 'ALL') params.status = status
          if (tab === 'alimtalk' && requestIdFilter.trim()) {
            const n = parseInt(requestIdFilter.trim(), 10)
            if (Number.isFinite(n) && n > 0) params.requestId = n
          }
          if (NOTICE_LOG_TABS.has(tab) && templateFilter && templateFilter !== 'ALL') {
            params.templateCode = templateFilter
          }
          const res = await api.get('/notice-logs', { params })
          const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
          const raw = Array.isArray(payload?.items) ? payload.items : []
          const payloadTemplateCodes = Array.isArray(payload?.templateCodes) ? payload.templateCodes : []
          const serverTotal = Number(payload?.total)
          items = raw.filter((row) => {
            const ch = String(row?.channel || '').toUpperCase()
            if (channels.length > 0 && !channels.includes(ch)) return false
            if ((tab === 'bulkSms' || tab === 'bulkKakao') && ch !== 'KAKAO_BULK' && !containsBulkWord(row)) return false
            if (status && status !== 'ALL' && String(row?.status || '').toUpperCase() !== status) return false
            if (sourceChannel !== 'ALL' && normalizeSourceChannel(row?.sourceChannel) !== sourceChannel) return false
            return true
          })
          total = sourceChannel !== 'ALL' || tab === 'bulkKakao'
            ? items.length
            : (Number.isFinite(serverTotal) ? serverTotal : items.length)
          if (!ignore) setResult({ items, total, actionCodes: [], templateCodes: payloadTemplateCodes })
          return
        }
        if (!ignore) setResult({ items, total, actionCodes: [] })
      } catch {
        if (!ignore) setError('이력 데이터를 불러오지 못했습니다')
      } finally {
        if (!ignore) setLoading(false)
      }
    }
    load()
    return () => { ignore = true }
  }, [tab, q, status, from, to, page, size, actionFilter, sourceChannel, requestIdFilter, templateFilter, refreshKey])

  const title = useMemo(() => {
    const found = TABS.find(t => t.key === tab)
    return found ? found.label : ''
  }, [tab])

  const rows = useMemo(() => (Array.isArray(result.items) ? result.items : []), [result.items])
  const actionOptions = useMemo(() => {
    if (tab !== 'change' && tab !== 'event') return []
    const allKnown = Object.keys(ACTION_LABELS).filter((code) => {
      return tab === 'event' ? isEventAction(code) : !isEventAction(code)
    })
    const loaded = Array.isArray(result?.actionCodes) ? result.actionCodes : []
    return Array.from(new Set([...allKnown, ...loaded])).sort()
  }, [result, tab])
  const templateOptions = useMemo(() => {
    if (!NOTICE_LOG_TABS.has(tab)) return []
    const loaded = Array.isArray(result?.templateCodes) ? result.templateCodes : []
    const visible = rows.map((row) => String(row?.templateCode || '').trim())
    const selected = templateFilter && templateFilter !== 'ALL' ? [templateFilter] : []
    return Array.from(new Set([...loaded, ...visible, ...selected].map(v => String(v || '').trim()).filter(Boolean))).sort()
  }, [result, rows, tab, templateFilter])

  const labelForAction = (code) => labelForActionCode(code)

  const labelForStatus = (raw) => {
    const code = String(raw || '').toUpperCase()
    return STATUS_LABELS[code] || (raw || '-')
  }

  const parseMeta = (row) => {
    try {
      const raw = String(row?.rawMessage || '').trim()
      if (!raw || !raw.startsWith('{')) return {}
      const obj = JSON.parse(raw)
      return obj && typeof obj === 'object' ? obj : {}
    } catch {
      return {}
    }
  }

  const pick = (obj, keys) => {
    for (const k of keys) {
      const v = obj?.[k]
      if (v !== undefined && v !== null && String(v).trim() !== '') return String(v)
    }
    return null
  }

  const displayMessage = (row) => {
    const msg = String(row?.messageText || '').trim()
    if (msg) return msg
    const raw = String(row?.rawMessage || '').trim()
    if (!raw) return '-'
    if (raw.startsWith('{')) {
      try {
        const o = JSON.parse(raw)
        if (o && typeof o === 'object') {
          const t = String(o.templateCode || '').trim()
          const parts = []
          if (t) parts.push(`템플릿: ${t}`)
          const rId = String(o.requestId || '').trim()
          const tgtId = String(o.targetId || '').trim()
          if (rId) parts.push(`의뢰 ID: ${rId}`)
          if (tgtId) parts.push(`타겟 ID: ${tgtId}`)
          const resp = String(o.response || '').trim()
          if (resp) {
            const sum = summarizeIntegrationResponse(resp)
            if (sum) parts.push(`연동: ${sum}`)
          }
          if (parts.length) return parts.join('\n')
        }
      } catch { /* fall through */ }
    }
    return raw.length > 140 ? `${raw.slice(0, 140)}...` : raw
  }

  const consentCheckedDetail = (row) => {
    const raw = String(row?.checkedSummary || '').trim()
    if (!raw) return '-'
    const prefix = raw.startsWith('설계사 확인 · ') ? '설계사 확인: 완료\n' : ''
    const jsonText = raw.replace(/^설계사 확인 ·\s*/, '')
    try {
      const parsed = JSON.parse(jsonText)
      const items = Array.isArray(parsed?.items)
        ? parsed.items
        : Array.isArray(parsed)
          ? parsed
          : [parsed]
      const lines = items.map((item) => {
        if (!item || typeof item !== 'object') return String(item)
        const doc = labelConsentDoc(item.docCode || item.id || item.kind)
        const version = item.versionLabel || item.version || item.docVersion || ''
        const accepted = item.accepted === false ? '미동의' : '동의'
        return `${doc}${version ? ` (${version})` : ''}: ${accepted}`
      })
      return `${prefix}${lines.join('\n')}`.trim()
    } catch {
      return `${prefix}${jsonText}`.trim()
    }
  }

  const labelForNotificationChannel = (code) => {
    const c = String(code || '').toUpperCase()
    return NOTIFICATION_CHANNEL_LABELS[c] || (code || '-')
  }

  const formatChannelResultsBlock = (cr) => {
    if (!cr || typeof cr !== 'object' || Array.isArray(cr)) return null
    const lines = []
    for (const [k, v] of Object.entries(cr)) {
      if (!v || typeof v !== 'object') continue
      const ok = v.success === true || v.success === 'true'
      const ec = v.errorCode != null && String(v.errorCode).trim() !== '' ? String(v.errorCode) : ''
      const rb = v.response != null && String(v.response).trim() !== '' ? String(v.response).trim() : ''
      const rbShow = rb ? summarizeIntegrationResponse(rb) : ''
      lines.push(`· ${k}: ${ok ? '성공' : '실패'}${ec ? ` (${ec})` : ''}${rbShow ? ` — ${rbShow}` : ''}`)
    }
    return lines.length ? lines.join('\n') : null
  }

  function alimtalkTemplateCode(row) {
    const meta = parseMeta(row)
    return String(row?.templateCode || pick(meta, ['templateCode']) || '').trim()
  }

  /** 목록 「내용」 칸: 한 줄 요약 (장문 연동 로그 제외) */
  const alimtalkListContentPreview = (row) => {
    const text = String(row?.messageText || '').trim()
    if (text) return text.length > 56 ? `${text.slice(0, 56)}…` : text
    const meta = parseMeta(row)
    const tc = alimtalkTemplateCode(row)
    const bits = []
    if (tc) bits.push(tc)
    const rid = pick(meta, ['requestId'])
    if (rid) bits.push(`의뢰 #${rid}`)
    const resp = pick(meta, ['response'])
    if (resp) {
      const sum = summarizeIntegrationResponse(resp)
      if (sum) bits.push(sum)
    }
    const out = bits.join(' · ')
    return out || '-'
  }

  /**
   * 상세 「내용」: 저장된 문구가 있으면 그대로.
   * 없으면(대부분 의뢰 배정형) 개인정보 보호로 본문 미저장 안내 + 연관 ID·연동 요약만.
   */
  const alimtalkModalBody = (row) => {
    const text = String(row?.messageText || '').trim()
    if (text) return text
    const meta = parseMeta(row)
    const tc = alimtalkTemplateCode(row)
    const lines = []
    if (tc === 'REQUEST_ASSIGNED') {
      lines.push('실제 카카오(알림톡)에 전달된 치환 문구(고객명·고지내역 등)는 개인정보 보호를 위해 이 시스템의 발송 로그에는 저장하지 않습니다.')
      lines.push('문구·변수는 승인된 카카오 템플릿과 해당 설계 의뢰 상세 정보를 기준으로 발송됩니다.')
    } else if (tc) {
      lines.push('이 로그에는 발송 본문 전체가 없을 수 있습니다. (저장 정책·채널에 따라 다름)')
    } else {
      lines.push('저장된 발송 문구가 없습니다.')
    }
    const rid = pick(meta, ['requestId'])
    const tid = pick(meta, ['targetId'])
    const uid = pick(meta, ['userId'])
    const refBits = []
    if (rid) refBits.push(`의뢰 ID ${rid}`)
    if (tid) refBits.push(`타겟 ID ${tid}`)
    if (uid) refBits.push(`수신 사용자 ID ${uid}`)
    if (refBits.length) lines.push(`연관 참조: ${refBits.join(', ')}`)
    const innerCh = pick(meta, ['channel'])
    if (innerCh) lines.push(`시도 채널(로그): ${labelForNotificationChannel(innerCh)} (${innerCh})`)
    const succ = pick(meta, ['success'])
    if (succ) lines.push(`전송 성공(로그): ${succ === 'true' ? '예' : '아니오'}`)
    const resp = pick(meta, ['response'])
    if (resp) {
      const sum = summarizeIntegrationResponse(resp)
      if (sum) lines.push(`연동 결과: ${sum}`)
    }
    const crBlock = formatChannelResultsBlock(meta?.channelResults)
    if (crBlock) lines.push(`채널별 결과:\n${crBlock}`)
    if (tc) lines.push(`템플릿 코드: ${tc}`)
    return lines.join('\n\n')
  }

  const alimtalkDetailWho = (row) => {
    const meta = parseMeta(row)
    const senderName = String(row?.senderName || '').trim()
    const senderId = String(row?.senderId || '').trim()
    if (senderName || senderId) {
      return [senderName || null, senderId ? `ID ${senderId}` : null].filter(Boolean).join(' · ') || '-'
    }
    const an = String(row?.actorName || pick(meta, ['actorName']) || '').trim()
    const aid = String(row?.actorUserId || pick(meta, ['actorUserId']) || '').trim()
    if (an || aid) {
      return [an || null, aid ? `사용자 ID ${aid}` : null].filter(Boolean).join(' · ') || '-'
    }
    return '-'
  }

  const alimtalkDetailRecipient = (row) => {
    const meta = parseMeta(row)
    const parts = []
    const rn = String(row?.recipientUserName || '').trim()
    const rid = row?.recipientUserId != null && row?.recipientUserId !== '' ? String(row.recipientUserId) : ''
    if (rn || rid) {
      if (rn) parts.push(rn)
      if (rid) parts.push(`(사용자 ID ${rid})`)
    }
    const phone = pick(meta, ['maskedPhone', 'phone', 'to', 'recipient'])
    if (phone) parts.push(`연락처(마스킹): ${phone}`)
    const grp = String(row?.recipientGroupName || pick(meta, ['recipientGroupName', 'groupName']) || '').trim()
    if (grp && !parts.some(p => p.includes(grp))) parts.push(`그룹/대상: ${grp}`)
    return parts.length ? parts.join('\n') : '-'
  }

  const alimtalkStatusSummary = (row) => {
    const bits = [labelForStatus(row.status)]
    const ec = String(row?.errorCode || '').trim()
    if (ec) bits.push(`오류 ${ec}`)
    if (row?.attemptCount != null && row.attemptCount !== '') bits.push(`시도 ${row.attemptCount}회`)
    return bits.join(' · ')
  }

  const inferSentCount = (row) => {
    const n = Number(row?.sentCount)
    if (Number.isFinite(n) && n >= 0) return n
    return row?.status === 'FAILED' ? 1 : 1
  }

  const inferSuccessCount = (row) => {
    const n = Number(row?.successCount)
    if (Number.isFinite(n) && n >= 0) return n
    return String(row?.status || '').toUpperCase() === 'SENT' ? 1 : 0
  }

  const inferFailCount = (row) => {
    const n = Number(row?.failCount)
    if (Number.isFinite(n) && n >= 0) return n
    return String(row?.status || '').toUpperCase() === 'FAILED' ? 1 : 0
  }

  const buildTemplateHref = (templateId) => {
    const v = String(templateId || '').trim()
    if (!v) return null
    return `/notices/templates?templateId=${encodeURIComponent(v)}`
  }

  const imageOx = (v) => (v === true ? 'O' : 'X')
  const normalizeSourceChannel = (v) => {
    const s = String(v || '').trim().toUpperCase()
    if (s === 'PC' || s === 'MOBILE_WEB' || s === 'APP' || s === 'WINDOW' || s === 'SYSTEM') return s
    return 'UNKNOWN'
  }
  const sourceChannelLabel = (v) => SOURCE_CHANNEL_LABELS[normalizeSourceChannel(v)] || '미분류'
  const faxProviderLabel = (provider) => {
    const code = String(provider || '').trim().toUpperCase()
    if (!code) return '팩스'
    return NOTIFICATION_CHANNEL_LABELS[code] || code
  }
  const faxErrorSummary = (row) => {
    const message = String(row?.lastErrorMessage || '').trim()
    const rawCode = row?.lastErrorCode
    const code = rawCode === undefined || rawCode === null || rawCode === '' ? '' : String(rawCode).trim()
    if (message && code) return `${message} (${code})`
    return message || code || '-'
  }
  const faxAttachments = (row) => (Array.isArray(row?.attachments) ? row.attachments : [])
  const faxAttachmentTypeLabel = (attachment) => {
    const contentType = String(attachment?.contentType || '').trim().toLowerCase()
    const name = String(attachment?.name || attachment?.filename || attachment?.originalName || attachment?.fileId || '').trim().toLowerCase()
    if (contentType.includes('pdf') || name.endsWith('.pdf')) return 'PDF'
    if (contentType.startsWith('image/') || /\.(png|jpe?g|gif|bmp|webp|tiff?)$/.test(name)) return '이미지'
    if (contentType) return contentType
    const dot = name.lastIndexOf('.')
    if (dot >= 0 && dot < name.length - 1) return name.slice(dot + 1).toUpperCase()
    return '기타'
  }
  const faxAttachmentTypes = (row) => {
    const types = Array.from(new Set(faxAttachments(row).map(faxAttachmentTypeLabel).filter(Boolean)))
    return types.length > 0 ? types.join(', ') : '-'
  }
  const faxAttachmentTotalSize = (row) => {
    const attachments = faxAttachments(row)
    if (attachments.length === 0) return '-'
    let hasKnownSize = false
    const total = attachments.reduce((sum, attachment) => {
      const raw = attachment?.size
      if (raw === undefined || raw === null || raw === '') return sum
      const n = Number(raw)
      if (!Number.isFinite(n) || n < 0) return sum
      hasKnownSize = true
      return sum + n
    }, 0)
    return hasKnownSize ? formatAuditBytes(total) : '-'
  }
  const faxNonnegativeNumber = (value) => {
    if (value === undefined || value === null || value === '') return null
    const n = Number(value)
    return Number.isFinite(n) && n >= 0 ? n : null
  }
  const faxPageCount = (row) => {
    const n = faxNonnegativeNumber(row?.pageCounts?.send)
    return n != null ? `${Math.round(n)}페이지` : '-'
  }
  const faxPageBreakdown = (row) => {
    const success = faxNonnegativeNumber(row?.pageCounts?.success)
    const fail = faxNonnegativeNumber(row?.pageCounts?.fail)
    const parts = []
    if (success != null) parts.push(`성공 ${Math.round(success)}페이지`)
    if (fail != null) parts.push(`실패 ${Math.round(fail)}페이지`)
    return parts.join(' / ')
  }
  const closeDetail = () => {
    detailRequestSeq.current += 1
    setDetailRow(null)
    setDetailLoading(false)
    setDetailError(null)
  }
  const openDetail = async (row) => {
    const seq = detailRequestSeq.current + 1
    detailRequestSeq.current = seq
    setDetailRow(row)
    setDetailError(null)
    if (tab !== 'fax' || !row?.id) {
      setDetailLoading(false)
      return
    }
    setDetailLoading(true)
    try {
      const res = await api.get(`/fax/transmissions/${encodeURIComponent(row.id)}`)
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      if (detailRequestSeq.current === seq) {
        setDetailRow({ ...row, ...(payload && typeof payload === 'object' ? payload : {}) })
      }
    } catch {
      if (detailRequestSeq.current === seq) {
        setDetailError('팩스 상세 정보를 불러오지 못했습니다')
      }
    } finally {
      if (detailRequestSeq.current === seq) {
        setDetailLoading(false)
      }
    }
  }
  const successRate = (successCount, sentCount) => {
    const s = Number(successCount)
    const t = Number(sentCount)
    if (!Number.isFinite(s) || !Number.isFinite(t) || t <= 0) return '-'
    return `${((s / t) * 100).toFixed(1)}%`
  }

  return (
    <div className="p-4">
      <div className="mb-4">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-semibold">발송데이터 관리</h1>
          <MenuGuideButton slug="ops-management" title="운영관리 가이드" />
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-2 border-b">
        {TABS.map(t => (
          <button
            key={t.key}
            type="button"
            className={`px-3 py-2 text-sm border-b-2 -mb-px ${tab === t.key ? 'border-brand-sky text-brand-sky font-semibold' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mb-3 flex flex-wrap gap-2">
        <input className="rounded border px-3 py-2 text-sm" placeholder="검색어" value={q} onChange={(e) => { setPage(0); setQ(e.target.value) }} />
        {tab === 'alimtalk' && (
          <input
            className="rounded border px-3 py-2 text-sm w-36"
            placeholder="의뢰 ID"
            inputMode="numeric"
            value={requestIdFilter}
            onChange={(e) => { setPage(0); setRequestIdFilter(e.target.value.replace(/\D/g, '')) }}
          />
        )}
        {NOTICE_LOG_TABS.has(tab) && (
          <select
            className="rounded border px-3 py-2 text-sm min-w-48"
            value={templateFilter}
            onChange={(e) => { setPage(0); setTemplateFilter(e.target.value) }}
          >
            <option value="ALL">템플릿 전체</option>
            {templateOptions.map((code) => (
              <option key={code} value={code}>{alimtalkMenuLabel(code)} ({code})</option>
            ))}
          </select>
        )}
        {(tab === 'change' || tab === 'event') && (
          <select className="rounded border px-3 py-2 text-sm" value={actionFilter} onChange={(e) => { setPage(0); setActionFilter(e.target.value) }}>
            <option value="">구분 전체</option>
            {actionOptions.map((code) => (
              <option key={code} value={code}>{labelForAction(code)}</option>
            ))}
          </select>
        )}
        {tab !== 'change' && tab !== 'event' && (
          <select className="rounded border px-3 py-2 text-sm" value={status} onChange={(e) => { setPage(0); setStatus(e.target.value) }}>
            <option value="ALL">상태 전체</option>
            <option value="SENT">발송완료</option>
            <option value="FAILED">실패</option>
            <option value="PENDING">대기</option>
            <option value="SUBMITTED">접수</option>
            <option value="DELIVERED">전달완료</option>
            <option value="CANCELLED">취소</option>
          </select>
        )}
        <select className="rounded border px-3 py-2 text-sm" value={sourceChannel} onChange={(e) => { setPage(0); setSourceChannel(e.target.value) }}>
          <option value="ALL">채널 전체</option>
          <option value="PC">PC</option>
          <option value="MOBILE_WEB">모바일 웹</option>
          <option value="APP">앱</option>
          <option value="WINDOW">Windows</option>
          <option value="SYSTEM">시스템</option>
          <option value="UNKNOWN">미분류</option>
        </select>
        <input className="rounded border px-3 py-2 text-sm" type="date" value={from} onChange={(e) => { setPage(0); setFrom(e.target.value) }} />
        <input className="rounded border px-3 py-2 text-sm" type="date" value={to} onChange={(e) => { setPage(0); setTo(e.target.value) }} />
        <button
          type="button"
          className="inline-flex items-center gap-2 rounded border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          onClick={() => setRefreshKey((k) => k + 1)}
          disabled={loading}
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          새로고침
        </button>
      </div>

      {loading && rows.length === 0 && <Loading />}
      {error && <InlineError message={error} />}

      {!error && (
        <div className="overflow-x-auto rounded border bg-white">
          {tab === 'bulkKakao' ? (
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left">
                <tr>
                  <th className="px-3 py-2">시간</th>
                  <th className="px-3 py-2">채널</th>
                  <th className="px-3 py-2">상태</th>
                  <th className="px-3 py-2">발송자</th>
                  <th className="px-3 py-2">발송자ID</th>
                  <th className="px-3 py-2">발송대상</th>
                  <th className="px-3 py-2">건수</th>
                  <th className="px-3 py-2">성공</th>
                  <th className="px-3 py-2">실패</th>
                  <th className="px-3 py-2">성공률</th>
                  <th className="px-3 py-2">이미지</th>
                  <th className="px-3 py-2">템플릿</th>
                  <th className="px-3 py-2">상세</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td className="px-3 py-5 text-center text-slate-500" colSpan={13}>{title} 데이터가 없습니다</td></tr>
                )}
                {rows.map((row) => (
                  <tr key={`${tab}-${row.id}`} className="border-t">
                    <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(row.createdAt)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{sourceChannelLabel(row.sourceChannel)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{labelForStatus(row.status)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{row.senderName || '-'}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{row.senderId || '-'}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{row.recipientGroupName || '-'}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{row.sentCount ?? '-'}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{row.successCount ?? '-'}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{row.failCount ?? '-'}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{successRate(row.successCount, row.sentCount)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{imageOx(row.imageIncluded)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {buildTemplateHref(row.templateId) ? (
                        <Link className="text-brand-sky hover:underline" to={buildTemplateHref(row.templateId)}>
                          {row.templateId}
                        </Link>
                      ) : (
                        <span>{row.templateCode || '-'}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <button type="button" className="rounded border px-2 py-1 text-xs hover:bg-slate-50" onClick={() => openDetail(row)}>상세</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : tab === 'bulkSms' ? (
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left">
                <tr>
                  <th className="px-3 py-2">시간</th>
                  <th className="px-3 py-2">채널</th>
                  <th className="px-3 py-2">상태</th>
                  <th className="px-3 py-2">발송대상</th>
                  <th className="px-3 py-2">건수</th>
                  <th className="px-3 py-2">성공</th>
                  <th className="px-3 py-2">실패</th>
                  <th className="px-3 py-2">성공률</th>
                  <th className="px-3 py-2">템플릿</th>
                  <th className="px-3 py-2">상세</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td className="px-3 py-5 text-center text-slate-500" colSpan={10}>{title} 데이터가 없습니다</td></tr>
                )}
                {rows.map((row) => {
                  const meta = parseMeta(row)
                  const sent = inferSentCount(row)
                  const succ = inferSuccessCount(row)
                  const fail = inferFailCount(row)
                  return (
                    <tr key={`${tab}-${row.id}`} className="border-t">
                      <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(row.createdAt)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{sourceChannelLabel(row.sourceChannel)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{labelForStatus(row.status)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{row.recipientGroupName || pick(meta, ['recipientGroupName', 'groupName', 'to']) || '-'}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{sent}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{succ}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{fail}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{successRate(succ, sent)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{row.templateCode || '-'}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <button type="button" className="rounded border px-2 py-1 text-xs hover:bg-slate-50" onClick={() => openDetail(row)}>상세</button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          ) : tab === 'alimtalk' ? (
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left">
                <tr>
                  <th className="px-3 py-2">발송 시각</th>
                  <th className="px-3 py-2">발송 요청자</th>
                  <th className="px-3 py-2">상태</th>
                  <th className="px-3 py-2">메뉴 · 유형</th>
                  <th className="px-3 py-2">채널</th>
                  <th className="px-3 py-2">발송 매체</th>
                  <th className="px-3 py-2">수신자</th>
                  <th className="px-3 py-2">내용</th>
                  <th className="px-3 py-2">상세</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td className="px-3 py-5 text-center text-slate-500" colSpan={9}>{title} 데이터가 없습니다</td></tr>
                )}
                {rows.map((row) => {
                  const meta = parseMeta(row)
                  const recs = normalizeNoticeRecipients(row)
                  let recv = '-'
                  if (recs.length === 1) recv = formatRecipientOneLine(recs[0])
                  else if (recs.length > 1) recv = `수신 ${recs.length}명 — ${formatRecipientOneLine(recs[0])} 외`
                  else recv = pick(meta, ['maskedPhone', 'phone', 'to', 'recipient']) || row.recipientUserName || row.recipientGroupName || '-'
                  return (
                    <tr key={`${tab}-${row.id}`} className="border-t">
                      <td className="px-3 py-2 whitespace-nowrap align-top">{formatDateTime(row.createdAt)}</td>
                      <td className="px-3 py-2 align-top whitespace-nowrap max-w-[10rem]">{alimtalkDetailWho(row)}</td>
                      <td className="px-3 py-2 align-top whitespace-nowrap">{labelForStatus(row.status)}</td>
                      <td className="px-3 py-2 align-top max-w-[11rem]">{alimtalkMenuLabel(alimtalkTemplateCode(row))}</td>
                      <td className="px-3 py-2 align-top whitespace-nowrap">{sourceChannelLabel(row.sourceChannel)}</td>
                      <td className="px-3 py-2 align-top whitespace-nowrap">{labelForNotificationChannel(row.channel)}</td>
                      <td className="px-3 py-2 align-top max-w-[14rem] break-words text-slate-800">{recv}</td>
                      <td className="px-3 py-2 align-top max-w-[14rem] text-slate-600 break-words">{alimtalkListContentPreview(row)}</td>
                      <td className="px-3 py-2 whitespace-nowrap align-top">
                        <button type="button" className="rounded border px-2 py-1 text-xs hover:bg-slate-50" onClick={() => openDetail(row)}>상세</button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          ) : tab === 'email' ? (
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left">
                <tr>
                  <th className="px-3 py-2">시간</th>
                  <th className="px-3 py-2">채널</th>
                  <th className="px-3 py-2">상태</th>
                  <th className="px-3 py-2">발송대상</th>
                  <th className="px-3 py-2">건수</th>
                  <th className="px-3 py-2">성공</th>
                  <th className="px-3 py-2">실패</th>
                  <th className="px-3 py-2">제목</th>
                  <th className="px-3 py-2">상세</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td className="px-3 py-5 text-center text-slate-500" colSpan={9}>{title} 데이터가 없습니다</td></tr>
                )}
                {rows.map((row) => {
                  const meta = parseMeta(row)
                  const sent = inferSentCount(row)
                  const succ = inferSuccessCount(row)
                  const fail = inferFailCount(row)
                  return (
                    <tr key={`${tab}-${row.id}`} className="border-t">
                      <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(row.createdAt)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{sourceChannelLabel(row.sourceChannel)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{labelForStatus(row.status)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{row.recipientGroupName || pick(meta, ['recipientGroupName', 'to', 'email']) || '-'}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{sent}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{succ}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{fail}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{row.title || pick(meta, ['subject', 'title']) || '-'}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <button type="button" className="rounded border px-2 py-1 text-xs hover:bg-slate-50" onClick={() => openDetail(row)}>상세</button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          ) : tab === 'fax' ? (
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left">
                <tr>
                  <th className="px-3 py-2">시간</th>
                  <th className="px-3 py-2">채널</th>
                  <th className="px-3 py-2">상태</th>
                  <th className="px-3 py-2">발신번호</th>
                  <th className="px-3 py-2">수신번호</th>
                  <th className="px-3 py-2">수신자</th>
                  <th className="px-3 py-2">제목</th>
                  <th className="px-3 py-2">오류</th>
                  <th className="px-3 py-2">상세</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td className="px-3 py-5 text-center text-slate-500" colSpan={9}>{title} 데이터가 없습니다</td></tr>
                )}
                {rows.map((row) => (
                  <tr key={`${tab}-${row.id}`} className="border-t">
                    <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(row.createdAt)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{faxProviderLabel(row.provider)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{labelForStatus(row.status)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{row.sendNum || '-'}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{row.receiveNum || '-'}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{row.receiveName || '-'}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{row.title || '-'}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{faxErrorSummary(row)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <button type="button" className="rounded border px-2 py-1 text-xs hover:bg-slate-50" onClick={() => openDetail(row)}>상세</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : tab === 'consent' ? (
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left">
                <tr>
                  <th className="px-3 py-2">동의 시각</th>
                  <th className="px-3 py-2">사용처</th>
                  <th className="px-3 py-2">회원</th>
                  <th className="px-3 py-2">대상</th>
                  <th className="px-3 py-2">문서/버전</th>
                  <th className="px-3 py-2">IP</th>
                  <th className="px-3 py-2">채널</th>
                  <th className="px-3 py-2">상세</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td className="px-3 py-5 text-center text-slate-500" colSpan={8}>동의이력 데이터가 없습니다</td></tr>
                )}
                {rows.map((row) => (
                  <tr key={`consent-${row.source}-${row.id}`} className="border-t">
                    <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(row.createdAt)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{row.source || '-'}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{row.userName || row.userEmail || row.userId || '-'}</td>
                    <td className="px-3 py-2 max-w-[14rem] break-words">{row.customerIdentifier || formatConsentTargetId(row.targetId)}</td>
                    <td className="px-3 py-2 max-w-[18rem] break-words">{formatConsentDocSummary(row.docSummary)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{row.ip || '-'}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{labelConsentChannel(row.channel)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <button type="button" className="rounded border px-2 py-1 text-xs hover:bg-slate-50" onClick={() => openDetail(row)}>상세</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left">
                <tr>
                  <th className="px-3 py-2">시간</th>
                  <th className="px-3 py-2">채널</th>
                  <th className="px-3 py-2">구분</th>
                  <th className="px-3 py-2">상세</th>
                  <th className="px-3 py-2">상태</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td className="px-3 py-5 text-center text-slate-500" colSpan={5}>{title} 데이터가 없습니다</td></tr>
                )}
                {rows.map((row) => (
                  <tr key={`${tab}-${row.id}`} className="border-t">
                    <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(row.createdAt)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{sourceChannelLabel(auditRowSourceChannel(row))}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {tab === 'change' || tab === 'event' ? labelForAction(row.action) : (row.channel || row.provider || '-')}
                    </td>
                    <td className="px-3 py-2">
                      {tab === 'change' || tab === 'event' ? (
                        (() => {
                          const am = parseAuditMeta(row)
                          const hasMeta = Boolean(am.detailKo || am.uploadSource || am.originalFileName || am.sizeBytes != null)
                          return (
                            <div>
                              {am.detailKo ? (
                                <div className="font-medium text-slate-800">{am.detailKo}</div>
                              ) : null}
                              <div className={am.detailKo ? 'text-sm text-slate-700 mt-0.5' : 'font-medium text-slate-800'}>
                                {formatAuditResource(row.resource)}
                              </div>
                              {hasMeta ? (
                                <div className="text-xs text-slate-500 mt-1 space-y-0.5">
                                  {am.uploadSource ? <div>저장 출처: {labelUploadSource(am.uploadSource)}</div> : null}
                                  {am.originalFileName ? <div>원본 파일명: {String(am.originalFileName)}</div> : null}
                                  {am.sizeBytes != null && am.sizeBytes !== '' ? <div>용량: {formatAuditBytes(am.sizeBytes)}</div> : null}
                                </div>
                              ) : null}
                              <div className="text-xs text-slate-500 mt-1">{row.userName || row.actor || '-'}</div>
                            </div>
                          )
                        })()
                      ) : tab === 'fax' ? (
                        <div>
                          <div className="font-medium">{row.title || '-'}</div>
                          <div className="text-xs text-slate-500">
                            수신자: {row.receiveName || '-'} / {row.receiveNum || '-'}
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div className="font-medium">{row.templateCode || row.title || '-'}</div>
                          {tab === 'bulkSms' ? (
                            <div className="mt-1 text-xs text-slate-600 space-y-0.5">
                              <div>대상: {row.recipientGroupName || '-'} / 총 {row.sentCount ?? '-'}명</div>
                              <div>성공 {row.successCount ?? '-'}건 / 실패 {row.failCount ?? '-'}건</div>
                              <div className="text-slate-700">문구: {displayMessage(row)}</div>
                            </div>
                          ) : (
                            <div className="text-xs text-slate-500">{row.errorCode || '-'}</div>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{labelForStatus(row.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <div className="mt-3 flex items-center justify-between text-sm">
        <div className="text-slate-600">총 {result.total ?? rows.length}건</div>
        <div className="flex gap-2">
          <button className="rounded border px-3 py-1 disabled:opacity-50" disabled={page <= 0} onClick={() => setPage(p => Math.max(0, p - 1))}>이전</button>
          <button className="rounded border px-3 py-1 disabled:opacity-50" disabled={(page + 1) * size >= (result.total ?? rows.length)} onClick={() => setPage(p => p + 1)}>다음</button>
        </div>
      </div>

      {detailRow && ['bulkKakao', 'bulkSms', 'alimtalk', 'email', 'fax', 'consent'].includes(tab) && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={closeDetail}>
          <div className="w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col rounded bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b flex items-center justify-between shrink-0">
              <div className="font-semibold">{tab === 'alimtalk' ? '알림톡 발송 상세' : tab === 'fax' ? '팩스 발송 상세' : tab === 'consent' ? '동의이력 상세' : '발송 상세'}</div>
              <button type="button" className="text-slate-500 hover:text-slate-700" onClick={closeDetail}>닫기</button>
            </div>
            <div className="p-4 text-sm overflow-y-auto">
              {tab === 'fax' && detailLoading ? (
                <div className="mb-3 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-slate-600">상세 정보를 불러오는 중입니다</div>
              ) : null}
              {detailError ? <div className="mb-3"><InlineError message={detailError} /></div> : null}
              {tab === 'alimtalk' ? (
                <div className="space-y-4">
                  <table className="w-full border border-slate-200 rounded-lg overflow-hidden border-collapse">
                    <thead>
                      <tr className="bg-slate-50 text-xs text-slate-600">
                        <th className="text-left font-semibold px-3 py-2 border-b border-slate-200 w-[34%]">발송 요청자</th>
                        <th className="text-left font-semibold px-3 py-2 border-b border-slate-200 w-[33%]">발송 시각</th>
                        <th className="text-left font-semibold px-3 py-2 border-b border-slate-200 w-[33%]">상태</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="bg-white">
                        <td className="px-3 py-2.5 align-top border-b border-slate-100 text-slate-800">{alimtalkDetailWho(detailRow)}</td>
                        <td className="px-3 py-2.5 align-top border-b border-slate-100 text-slate-800 whitespace-nowrap">{formatDateTime(detailRow.createdAt) || '-'}</td>
                        <td className="px-3 py-2.5 align-top border-b border-slate-100 text-slate-800">{alimtalkStatusSummary(detailRow)}</td>
                      </tr>
                    </tbody>
                  </table>

                  <table className="w-full border border-slate-200 rounded-lg overflow-hidden border-collapse">
                    <thead>
                      <tr className="bg-slate-50 text-xs text-slate-600">
                        <th className="text-left font-semibold px-3 py-2 border-b border-slate-200 w-1/2">메뉴 · 유형</th>
                        <th className="text-left font-semibold px-3 py-2 border-b border-slate-200 w-1/2">채널 (PC / 앱 등)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="bg-white">
                        <td className="px-3 py-2.5 align-top border-b border-slate-100">
                          <div className="text-slate-800">{alimtalkMenuLabel(alimtalkTemplateCode(detailRow))}</div>
                          <div className="text-xs text-slate-500 mt-1">
                            발송 매체: {labelForNotificationChannel(detailRow.channel)}
                            {detailRow.channel ? ` (${detailRow.channel})` : ''}
                          </div>
                        </td>
                        <td className="px-3 py-2.5 align-top border-b border-slate-100 text-slate-800">{sourceChannelLabel(detailRow.sourceChannel)}</td>
                      </tr>
                    </tbody>
                  </table>

                  <div>
                    <div className="text-xs font-semibold text-slate-500 mb-1">수신자 목록</div>
                    {(() => {
                      const recs = normalizeNoticeRecipients(detailRow)
                      const meta = parseMeta(detailRow)
                      const reqId = pick(meta, ['requestId'])
                      const tc = alimtalkTemplateCode(detailRow)
                      return (
                        <>
                          {tc === 'REQUEST_ASSIGNED' && reqId && (
                            <p className="text-xs text-slate-500 mb-2 leading-relaxed">
                              설계 의뢰 배정 알림은 원수사 타겟(배정 매니저)마다 로그가 한 건씩 쌓입니다.
                              동일 의뢰의 다른 수신 건은 목록 상단에서
                              {' '}
                              <span className="font-medium text-slate-700">의뢰 ID {reqId}</span>
                              를 입력해 한꺼번에 볼 수 있습니다.
                            </p>
                          )}
                          {recs.length === 0 ? (
                            <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-slate-800 whitespace-pre-wrap">
                              {alimtalkDetailRecipient(detailRow)}
                            </div>
                          ) : (
                            <ol className="m-0 list-decimal space-y-2 pl-5 text-slate-800 marker:text-slate-400">
                              {recs.map((r, idx) => (
                                <li key={idx} className="pl-1">
                                  <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                                    <div className="grid grid-cols-[5.5rem_1fr] gap-x-2 gap-y-1 items-baseline">
                                      <span className="text-xs text-slate-500">구분</span>
                                      <span>{String(r.orgTypeLabel || r.orgType || '').trim() || '-'}</span>
                                      <span className="text-xs text-slate-500">조직</span>
                                      <span className="break-words">{String(r.orgName || '').trim() || '-'}</span>
                                      <span className="text-xs text-slate-500">이름</span>
                                      <span>{String(r.name || '').trim() || '-'}</span>
                                      <span className="text-xs text-slate-500">사용자 ID</span>
                                      <span>{r.userId != null && String(r.userId).trim() !== '' ? String(r.userId) : '-'}</span>
                                      <span className="text-xs text-slate-500">연락처</span>
                                      <span>
                                        {[r.phoneTail, r.phoneMask].map((x) => String(x || '').trim()).filter(Boolean).join(' · ') || '-'}
                                      </span>
                                    </div>
                                  </div>
                                </li>
                              ))}
                            </ol>
                          )}
                        </>
                      )
                    })()}
                  </div>

                  <div>
                    <div className="text-xs font-semibold text-slate-500 mb-1">내용</div>
                    <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-slate-800 whitespace-pre-wrap">{alimtalkModalBody(detailRow)}</div>
                  </div>
                </div>
              ) : tab === 'fax' ? (
                <div className="space-y-4">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-xs font-semibold text-slate-500">발송 채널</div>
                      <div className="mt-1 text-slate-900">{faxProviderLabel(detailRow.provider)}</div>
                    </div>
                    <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-xs font-semibold text-slate-500">상태</div>
                      <div className="mt-1 text-slate-900">{labelForStatus(detailRow.status)}</div>
                    </div>
                    <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-xs font-semibold text-slate-500">발송 시각</div>
                      <div className="mt-1 text-slate-900">{formatDateTime(detailRow.createdAt) || '-'}</div>
                    </div>
                    <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-xs font-semibold text-slate-500">완료 시각</div>
                      <div className="mt-1 text-slate-900">{formatDateTime(detailRow.completedAt) || '-'}</div>
                    </div>
                    <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-xs font-semibold text-slate-500">발신번호</div>
                      <div className="mt-1 text-slate-900">{detailRow.sendNum || '-'}</div>
                    </div>
                    <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-xs font-semibold text-slate-500">수신번호</div>
                      <div className="mt-1 text-slate-900">{detailRow.receiveNum || '-'}</div>
                    </div>
                    <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-xs font-semibold text-slate-500">수신자</div>
                      <div className="mt-1 text-slate-900">{detailRow.receiveName || '-'}</div>
                    </div>
                    <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-xs font-semibold text-slate-500">제목</div>
                      <div className="mt-1 break-words text-slate-900">{detailRow.title || '-'}</div>
                    </div>
                    <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-xs font-semibold text-slate-500">요청번호</div>
                      <div className="mt-1 break-all text-slate-900">{detailRow.requestNum || '-'}</div>
                    </div>
                    <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-xs font-semibold text-slate-500">접수번호</div>
                      <div className="mt-1 break-all text-slate-900">{detailRow.receiptNum || '-'}</div>
                    </div>
                    <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-xs font-semibold text-slate-500">첨부 유형</div>
                      <div className="mt-1 text-slate-900">{faxAttachmentTypes(detailRow)}</div>
                    </div>
                    <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-xs font-semibold text-slate-500">첨부 개수</div>
                      <div className="mt-1 text-slate-900">{faxAttachments(detailRow).length}개</div>
                    </div>
                    <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-xs font-semibold text-slate-500">첨부 용량</div>
                      <div className="mt-1 text-slate-900">{faxAttachmentTotalSize(detailRow)}</div>
                    </div>
                    <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-xs font-semibold text-slate-500">전체 발송 페이지</div>
                      <div className="mt-1 text-slate-900">{faxPageCount(detailRow)}</div>
                      {faxPageBreakdown(detailRow) ? <div className="mt-1 text-xs text-slate-500">{faxPageBreakdown(detailRow)}</div> : null}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-slate-500 mb-1">첨부 파일</div>
                    {(() => {
                      const attachments = faxAttachments(detailRow)
                      if (attachments.length === 0) {
                        return <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-slate-800">-</div>
                      }
                      return (
                        <div className="rounded border border-slate-200 bg-slate-50 px-3 py-1.5 text-slate-800 divide-y divide-slate-200">
                          {attachments.map((attachment, index) => (
                            <div key={attachment.id || attachment.fileId || index} className="py-2">
                              <div className="font-medium break-words">{attachment.name || attachment.fileId || `파일 ${index + 1}`}</div>
                              <div className="mt-1 text-xs text-slate-500">
                                {faxAttachmentTypeLabel(attachment)}
                                {' / '}
                                {attachment.size != null && attachment.size !== '' ? formatAuditBytes(attachment.size) : '-'}
                              </div>
                            </div>
                          ))}
                        </div>
                      )
                    })()}
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-slate-500 mb-1">오류</div>
                    <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-slate-800 whitespace-pre-wrap">{faxErrorSummary(detailRow)}</div>
                  </div>
                </div>
              ) : tab === 'consent' ? (
                <div className="space-y-4">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-xs font-semibold text-slate-500">사용처</div>
                      <div className="mt-1 text-slate-900">{detailRow.source || '-'}</div>
                    </div>
                    <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-xs font-semibold text-slate-500">동의 시각</div>
                      <div className="mt-1 text-slate-900">{formatDateTime(detailRow.createdAt) || '-'}</div>
                    </div>
                    <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-xs font-semibold text-slate-500">회원</div>
                      <div className="mt-1 text-slate-900">{detailRow.userName || detailRow.userEmail || detailRow.userId || '-'}</div>
                      {detailRow.userEmail ? <div className="mt-1 text-xs text-slate-500">{detailRow.userEmail}</div> : null}
                    </div>
                    <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-xs font-semibold text-slate-500">대상</div>
                      <div className="mt-1 break-words text-slate-900">{detailRow.customerIdentifier || formatConsentTargetId(detailRow.targetId)}</div>
                    </div>
                    <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-xs font-semibold text-slate-500">문서/버전</div>
                      <div className="mt-1 break-words text-slate-900">{formatConsentDocSummary(detailRow.docSummary)}</div>
                    </div>
                    <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-xs font-semibold text-slate-500">접속 정보</div>
                      <div className="mt-1 text-slate-900">IP: {detailRow.ip || '-'}</div>
                      <div className="mt-1 text-xs text-slate-500">채널: {labelConsentChannel(detailRow.channel)}</div>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-slate-500 mb-1">체크 항목</div>
                    <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-slate-800 whitespace-pre-wrap">{consentCheckedDetail(detailRow)}</div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-slate-500 mb-1">User-Agent</div>
                    <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 break-all text-slate-800">{detailRow.userAgent || '-'}</div>
                  </div>
                </div>
              ) : (
                <div className="rounded border bg-slate-50 px-3 py-2 whitespace-pre-wrap">{displayMessage(detailRow)}</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
